import dayjs from 'dayjs';
import moment, { Moment } from 'moment';
import { memo, useCallback, useEffect, useState } from 'react';

import { MultiStoreCenterDay } from '../../../../interface/multi-store';
import { getClient } from '../../../../lib/info';
import {
  resetAppointmentSlot,
  selectedDate,
  selectedHour,
  serHourSlot,
  setDateSlot,
} from '../../../../store/appointment-slot';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  selectedMultiStoreAnyUserPointOfSaleId,
  switchMultiStorePointOfSale,
} from '../../../../store/multi-store';
import { responsiveWidth } from '../../../../store/responsive';
import { findClosestCenterWithHour } from '../../../../utils/multi-store/centers';
import {
  findNextActiveDay,
  STORE_DATE_FORMAT,
} from '../../../../utils/multi-store/dates';
import { getElsewhereOnly } from '../../../../utils/multi-store/elsewhere';
import { trackEvent } from '../../../../utils/user-tracking';
import { getWeekRange } from '../../../../utils/week-range';
import { MultiStoreDayTitle } from './multi-store-day-title';
import { MultiStoreHourAvailability } from './multi-store-hour-availability';
import { MultiStoreHours } from './multi-store-hours';
import { MultiStoreLegend } from './multi-store-legend';
import { trackMultiStore } from './multi-store-tracking';
import { MultiStoreWeek } from './multi-store-week';
import {
  useIncludedCenters,
  useMultiStoreDay,
  useMultiStoreWeek,
} from './use-multi-store';

// Calendario unificato dei centri inclusi: giorni e orari sono l'unione dei centri, la
// settimana ha ampiezza e navigazione del WeekPicker classico; i giorni e gli orari liberi
// solo negli altri centri hanno il puntino
export const MultiStoreCalendar = memo(
  function MultiStoreCalendar(): JSX.Element {
    const {
      company: { maxBookingValue },
    } = getClient();
    const dispatch = useAppDispatch();
    const selectedDay = useAppSelector(selectedDate);
    const hourSelected = useAppSelector(selectedHour);
    // centro corrente raggiunto passando a "qualsiasi operatore" (cambio centro o ritorno
    // all'origin dal pannello): la sua scheda lo ricorda
    const anyUserCenterId = useAppSelector(
      selectedMultiStoreAnyUserPointOfSaleId
    );
    const width = useAppSelector(responsiveWidth);
    const centers = useIncludedCenters();

    const [weekStart, setWeekStart] = useState<Moment>(() =>
      selectedDay === undefined
        ? moment().startOf('day')
        : moment(selectedDay, STORE_DATE_FORMAT).startOf('day')
    );
    const [isPrevButtonClicked, setIsPrevButtonClicked] = useState(false);

    const range = getWeekRange(width);
    const startDate = weekStart.format(STORE_DATE_FORMAT);
    const endDate = weekStart
      .clone()
      .add(range, 'days')
      .format(STORE_DATE_FORMAT);
    const maxDay = moment().startOf('day').add(maxBookingValue, 'hours');

    const week = useMultiStoreWeek(startDate, endDate, centers);
    const day = useMultiStoreDay(selectedDay, centers);

    // Stessa regola del WeekPicker: se il giorno scelto non è libero in nessun centro si passa
    // al prossimo giorno libero, anche nelle finestre successive, fino al massimo prenotabile.
    // Si aspettano tutti i centri: un giorno libero solo in un centro ancora in caricamento
    // non va saltato.
    useEffect(() => {
      if (
        !week.allSettled ||
        week.data === undefined ||
        selectedDay === undefined ||
        !moment(endDate, STORE_DATE_FORMAT).isBefore(
          moment().startOf('day').add(maxBookingValue, 'hours')
        )
      ) {
        return;
      }
      const next = findNextActiveDay(week.data, selectedDay);
      if (next === undefined) return;
      if (next.isInWindow) {
        dispatch(setDateSlot(next.date));
        return;
      }
      // dopo la freccia indietro non si salta alla finestra successiva
      if (isPrevButtonClicked) return;
      setWeekStart(moment(next.date, STORE_DATE_FORMAT));
      dispatch(setDateSlot(next.date));
    }, [
      dispatch,
      endDate,
      isPrevButtonClicked,
      maxBookingValue,
      selectedDay,
      week.allSettled,
      week.data,
    ]);

    // Come nell'HourPicker: con i dati del centro corrente caricati, un'ora che lì non è
    // libera si azzera (per esempio dopo un cambio centro)
    useEffect(() => {
      if (
        day.isCurrentSettled &&
        hourSelected !== undefined &&
        !day.currentTimes.includes(hourSelected)
      ) {
        dispatch(resetAppointmentSlot());
      }
    }, [day.currentTimes, day.isCurrentSettled, dispatch, hourSelected]);

    const handlePrev = useCallback(() => {
      setIsPrevButtonClicked(true);
      const newStart = weekStart.clone().subtract(range + 1, 'days');
      setWeekStart(newStart);
      dispatch(setDateSlot(newStart.format(STORE_DATE_FORMAT)));
    }, [dispatch, range, weekStart]);

    const handleNext = useCallback(() => {
      setIsPrevButtonClicked(false);
      const newStart = weekStart.clone().add(range + 1, 'days');
      setWeekStart(newStart);
      dispatch(setDateSlot(newStart.format(STORE_DATE_FORMAT)));
    }, [dispatch, range, weekStart]);

    const handleSelectDay = useCallback(
      (date: string) => {
        dispatch(setDateSlot(date));
      },
      [dispatch]
    );

    // Cambio centro tenendo giorno e ora; se l'operatore scelto non lavora nel centro si passa
    // al primo operatore disponibile, e il thunk lo ricorda per la scheda del centro
    const handleSwitch = useCallback(
      (center: MultiStoreCenterDay, hour: string) => {
        if (selectedDay === undefined) return;
        dispatch(
          switchMultiStorePointOfSale(
            center.pointOfSale,
            center.isAnyUser,
            selectedDay,
            hour
          )
        );
        trackMultiStore(`cambio centro ${center.pointOfSale.name}`);
      },
      [dispatch, selectedDay]
    );

    // Orario libero nel centro corrente: resta lì. Altrimenti si passa al centro incluso
    // più vicino che lo ha (l'origin ha distanza 0)
    const handleHour = useCallback(
      (hour: string) => {
        if (day.currentTimes.includes(hour)) {
          dispatch(serHourSlot(hour));
          trackEvent({
            action: 'Azione utente',
            category: 'selezione data e ora',
            label: `data ${dayjs(selectedDay).format('DD/MM/YYYY')} ora ${hour}`,
            url: location.pathname,
          });
          return;
        }
        const target = findClosestCenterWithHour(day.centers, hour);
        if (target === undefined) return;
        trackMultiStore(`orario ${hour} scelto in ${target.pointOfSale.name}`);
        handleSwitch(target, hour);
      },
      [day.centers, day.currentTimes, dispatch, handleSwitch, selectedDay]
    );

    const elsewhereHours = getElsewhereOnly(
      day.currentTimes,
      day.centers
        .filter((center) => !center.isCurrent)
        .map((center) => center.times),
      !day.isPending
    );
    const isLegendVisible =
      week.elsewhereDates.length > 0 || elsewhereHours.length > 0;

    return (
      <>
        <MultiStoreWeek
          data={week.data}
          elsewhereDates={week.elsewhereDates}
          selectedDay={selectedDay}
          isPending={week.isPending}
          isPrevDisabled={!moment().isBefore(weekStart)}
          isNextDisabled={weekStart.isAfter(maxDay)}
          onPrev={handlePrev}
          onNext={handleNext}
          onSelectDay={handleSelectDay}
        />
        <MultiStoreDayTitle day={selectedDay} />
        <MultiStoreHours
          hours={day.hours}
          elsewhereHours={elsewhereHours}
          hourSelected={hourSelected}
          isPending={day.isPending}
          isAfterMaxDay={moment(selectedDay, STORE_DATE_FORMAT).isSameOrAfter(
            moment().add(maxBookingValue, 'hours')
          )}
          loadingIds={day.loadingIds}
          onSelect={handleHour}
        />
        {isLegendVisible && <MultiStoreLegend />}
        <MultiStoreHourAvailability
          hour={day.isPending ? undefined : hourSelected}
          day={selectedDay}
          included={centers}
          centers={day.centers}
          unavailableIds={day.unavailableIds}
          anyUserCenterId={anyUserCenterId}
          onSwitch={handleSwitch}
        />
      </>
    );
  }
);
